import { useStore } from '@vromo/mobx-react';
import { User, Vehicle } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Input from '@vromo/ui/components/Input';
import { useModals } from '@vromo/ui/components/Modals';
import ProfileImage from '@vromo/ui/components/ProfileImage';
import Select from '@vromo/ui/components/Select';
import theme from '@vromo/ui/components/theme';
import { useToasts } from '@vromo/ui/components/Toasts';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { ChangeEvent, useState } from 'react';
import { update } from 'serializr';
import { RootStore } from '../stores/RootStore';
import { qaLabelTeams } from '../../../../common-qa-labels/teams';
import { buildDynamicLabel } from '../../../../common-qa-labels/utils';
import styles from './teamEditor.style';

type Props = {
    vehicle: Vehicle;
};

type BaseDriverRowProps = {
    vehicle: Vehicle;
    edit?: () => void;
    isHovering?: boolean;
    setIsHovering?: (isHovering: boolean) => void;
    deleteVehicle?: () => void;
};

const renderDetails = (driver: User | null) => {
    return (
        <>
            <td data-testid={qaLabelTeams.driverRowImage} className={styles.rowImage}>
                <ProfileImage url={driver?.avatar?.url} user={driver} size={34} />
            </td>
            <td data-testid={qaLabelTeams.driverRowNameText} className={styles.rowNameText}>
                {driver?.displayName}
            </td>
            <td data-testid={qaLabelTeams.driverRowEmailText}>{driver?.contact?.email}</td>
            <td data-testid={qaLabelTeams.driverRowPhoneText}>{driver?.contact?.phone}</td>
        </>
    );
};

export const BaseDriverRow = observer(
    ({ vehicle, edit, isHovering, setIsHovering, deleteVehicle }: BaseDriverRowProps) => {
        const { groupStore } = useStore<RootStore>('rootStore');
        const group = groupStore.groups.find(({ id }) => id === vehicle.group)?.name;
        const displayName = vehicle.driver?.displayName?.toString();

        const hoverProps = !!setIsHovering
            ? {
                  onMouseOver: () => setIsHovering(true),
                  onMouseOut: () => setIsHovering(false)
              }
            : {};

        return (
            <tr
                onClick={edit}
                data-testid={buildDynamicLabel(qaLabelTeams.driverRowPrefix, displayName)}
                key={vehicle.id}
                {...hoverProps}
                className={styles.rowContainer}>
                {renderDetails(vehicle?.driver)}
                <td data-testid={qaLabelTeams.driverRowGroupRow}>{group}</td>
                <td data-testid={qaLabelTeams.driverRowExternalIdRow}>{vehicle.external}</td>
                {!!deleteVehicle && (
                    <td>
                        <Button
                            small
                            onClick={deleteVehicle}
                            style={{ boxShadow: 'none', opacity: isHovering ? 1 : 0 }}
                            data-testid={qaLabelTeams.driverRowDeleteButton}
                            color={theme.grey}>
                            <FontAwesome icon="trash" style={{ color: theme.mediumGrey }} />
                        </Button>
                    </td>
                )}
            </tr>
        );
    }
);

export default observer(function EditableDriverRow({ vehicle }: Props) {
    const rootStore = useStore<RootStore>('rootStore');

    const [open, setOpen] = useState(false);
    const [group, setGroup] = useState(vehicle.group);
    const [external, setExternal] = useState(vehicle.external?.toString());
    const [isHovering, setIsHovering] = useState(false);

    const modals = useModals();
    const showToast = useToasts();

    const {
        authStore: { graph, role },
        groupStore,
        vehicleStore
    } = rootStore;

    async function deleteVehicle(e: React.MouseEvent<HTMLButtonElement>) {
        e.stopPropagation();
        if (
            await modals.confirm(
                'Remove Driver',
                `Are you sure you want to remove ${
                    vehicle.driver?.displayName || 'this driver'
                } from this group?`
            )
        ) {
            graph.root
                .graph()
                .roles(role.id)
                .vehicles(vehicle.id)
                .delete()
                .then(() => {
                    runInAction(() => {
                        update(rootStore.vehicleStore.vehicles, {
                            items: [{ id: vehicle.id }],
                            type: 'delete'
                        });
                    });
                })
                .catch((e) => {
                    console.error(e);
                    showToast(
                        'Something went wrong with deleting this driver. Please try again.',
                        'error'
                    );
                });
        }
    }

    function save(e: React.MouseEvent<HTMLButtonElement>) {
        const changes = new Array<Partial<Vehicle>>();
        const existsInGroup = vehicleStore
            .withGroup(group!)
            .some((v) => v.driver?.id === vehicle.driver?.id);

        if (!existsInGroup) {
            changes.push({ group });
        }
        if (external !== vehicle.external) {
            changes.push({ external });
        }

        if (!changes.length) {
            close(e);
            return;
        }

        const vehicleDiff = Object.assign({}, ...changes);

        graph.root
            .graph()
            .roles(role.id)
            .vehicles(vehicle.id)
            .post(vehicleDiff)
            .then(() =>
                Object.keys(vehicleDiff).forEach((key) => {
                    switch (key) {
                        case 'group':
                            showToast(
                                `${vehicle.displayName(
                                    vehicleStore.driverVehicleMap,
                                    groupStore.withId(vehicle.group!)
                                )} was successfully moved to ${groupStore.withId(group!)?.name}.`
                            );
                            break;
                        case 'external':
                            showToast('Updated external identifier');
                            break;
                    }
                })
            )
            .catch((e) => {
                console.error(e);
                showToast(
                    'Something went wrong with moving this driver to another group. Please try again.',
                    'error'
                );
            });
        close(e);
    }

    function edit() {
        setOpen(true);
    }

    function close(e: React.MouseEvent<HTMLButtonElement>) {
        e.stopPropagation();
        setOpen(false);
    }

    if (!open) {
        return <BaseDriverRow {...{ vehicle, isHovering, setIsHovering, deleteVehicle, edit }} />;
    }

    return (
        <tr
            data-testid={buildDynamicLabel(
                qaLabelTeams.driverRowPrefix,
                vehicle.driver?.displayName?.toString()
            )}
            key={vehicle.id}
            onClick={edit}
            className={styles.rowContainer}>
            {renderDetails(vehicle?.driver)}
            <td data-testid={qaLabelTeams.driverRowGroupRow}>
                <Select
                    data-testid={qaLabelTeams.driverRowGroupDropdown}
                    className="form-control input-sm"
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setGroup(parseInt(e.target.value))
                    }>
                    {rootStore.groupStore.groups.map((it) => (
                        <option key={it.id} value={it.id!} selected={vehicle.group == it.id}>
                            {it.name}
                        </option>
                    ))}
                </Select>
            </td>
            <td data-testid={qaLabelTeams.driverRowExternalIdRow}>
                <Input
                    data-testid={qaLabelTeams.driverRowExternalIdInput}
                    type="text"
                    value={external?.toString()}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setExternal(e.target.value)}
                    placeholder="e.g. 05275325"
                />
            </td>
            <td>
                <Button data-testid={qaLabelTeams.driverRowCancelBtn} onClick={close} small>
                    Cancel
                </Button>
                <Button data-testid={qaLabelTeams.driverRowSaveButton} onClick={save} small>
                    Save
                </Button>
            </td>
        </tr>
    );
});
